import { addMinutes } from 'date-fns';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MINUTES_PER_DAY = 24 * 60;
export const DEFAULT_SLEEP_GOAL_MINUTES = 8 * 60;
export const CONSISTENCY_HISTORY_NIGHTS = 13;
export const MIN_INTERRUPTION_MINUTES = 1;
export const DURATION_MAX_POINTS = 50;
export const CONSISTENCY_MAX_POINTS = 30;
export const INTERRUPTION_MAX_POINTS = 20;
export const SLEEP_SCORE_MAX = 100;

const UNDER_SLEEP_DECAY_RATE = 2.8;
const MILD_OVERSLEEP_DECAY_RATE = 0.6;
const LONG_OVERSLEEP_THRESHOLD_RATIO = 0.15;
const LONG_OVERSLEEP_DECAY_RATE = 2.0;
const CONSISTENCY_DEVIATION_SCALE_MINUTES = 90;
const MAX_VARIABILITY_PENALTY_RATIO = 0.2;
const VARIABILITY_SCALE_MINUTES = 75;
const INTERRUPTION_WASO_WEIGHT = 0.045;
const INTERRUPTION_COUNT_WEIGHT = 0.35;
const INTERRUPTION_LONGEST_WEIGHT = 0.08;
const INTERRUPTION_PENALTY_SCALE = 1.0;
export const SCORE_HIGH_MIN = 61;
export const SCORE_MODERATE_MIN = 41;

const SLEEP_STAGE_DEEP = 1;
const SLEEP_STAGE_LIGHT = 2;
const SLEEP_STAGE_REM = 3;
const SLEEP_STAGE_AWAKE = 4;
const TWO_PI = 2 * Math.PI;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SleepInterruption {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
}

export interface SleepSession {
  sleepStart: Date;
  sleepEnd: Date;
  sleepOnset: Date;
  totalSleepMinutes: number;
  interruptions: SleepInterruption[];
  awakeningCount: number;
  totalWasoMinutes: number;
  longestAwakeningMinutes: number;
}

export interface SleepScoreInput {
  currentSession: SleepSession;
  sleepGoalMinutes: number;
  previousSleepOnsets: Date[];
}

export type SleepScoreClassification = 'High' | 'Moderate' | 'Low';

export interface SleepScoreResult {
  sleepScore: number;
  classification: SleepScoreClassification;
  durationScore: number;
  consistencyScore: number;
  interruptionScore: number;
  explanation: string;
}

export interface SleepScoreMetricDetails {
  duration: string;
  consistency: string;
  interruptions: string;
}

export interface SleepStagePoint {
  tsMs: number;
  value: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const isValidDate = (value: Date): boolean =>
  value instanceof Date && Number.isFinite(value.getTime());

const isSleepStage = (value: number): boolean =>
  value === SLEEP_STAGE_DEEP || value === SLEEP_STAGE_LIGHT || value === SLEEP_STAGE_REM;

// ---------------------------------------------------------------------------
// Circular statistics (bedtimes crossing midnight)
// ---------------------------------------------------------------------------

const minutesToRadians = (minutes: number): number =>
  ((minutes % MINUTES_PER_DAY) / MINUTES_PER_DAY) * TWO_PI;

const radiansToMinutes = (radians: number): number => {
  let minutes = (radians / TWO_PI) * MINUTES_PER_DAY;
  if (minutes < 0) minutes += MINUTES_PER_DAY;
  return minutes % MINUTES_PER_DAY;
};

const dateToMinutesOfDay = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;

const circularMeanMinutes = (minutes: number[]): number | null => {
  if (minutes.length === 0) return null;
  let sinSum = 0;
  let cosSum = 0;
  for (const minute of minutes) {
    const radians = minutesToRadians(minute);
    sinSum += Math.sin(radians);
    cosSum += Math.cos(radians);
  }
  const count = minutes.length;
  return radiansToMinutes(Math.atan2(sinSum / count, cosSum / count));
};

const circularDeviationMinutes = (a: number, b: number): number => {
  const diff = Math.abs(a - b);
  return Math.min(diff, MINUTES_PER_DAY - diff);
};

const circularStdDevMinutes = (minutes: number[]): number => {
  if (minutes.length < 2) return 0;
  const mean = circularMeanMinutes(minutes);
  if (mean == null) return 0;
  let sumSq = 0;
  for (const minute of minutes) {
    const deviation = circularDeviationMinutes(minute, mean);
    sumSq += deviation * deviation;
  }
  return Math.sqrt(sumSq / minutes.length);
};

// ---------------------------------------------------------------------------
// Component scores
// ---------------------------------------------------------------------------

const calculateDurationScore = (totalSleepMinutes: number, sleepGoalMinutes: number): number => {
  if (!Number.isFinite(totalSleepMinutes) || totalSleepMinutes < 0) return 0;
  if (!Number.isFinite(sleepGoalMinutes) || sleepGoalMinutes <= 0) return 0;

  const ratio = totalSleepMinutes / sleepGoalMinutes;
  let rawScore: number;

  if (ratio <= 1) {
    rawScore = DURATION_MAX_POINTS * Math.exp(-UNDER_SLEEP_DECAY_RATE * (1 - ratio));
  } else {
    const oversleepRatio = ratio - 1;
    const decayRate = oversleepRatio <= LONG_OVERSLEEP_THRESHOLD_RATIO
      ? MILD_OVERSLEEP_DECAY_RATE
      : LONG_OVERSLEEP_DECAY_RATE;
    rawScore = DURATION_MAX_POINTS * Math.exp(-decayRate * oversleepRatio);
  }

  return Math.round(clamp(rawScore, 0, DURATION_MAX_POINTS));
};

const calculateConsistencyScore = (
  currentSleepOnset: Date,
  previousSleepOnsets: Date[]
): number => {
  if (previousSleepOnsets.length === 0) return CONSISTENCY_MAX_POINTS;

  const historicalMinutes = previousSleepOnsets.map(dateToMinutesOfDay);
  const habitualMean = circularMeanMinutes(historicalMinutes);
  if (habitualMean == null) return CONSISTENCY_MAX_POINTS;

  const tonightMinutes = dateToMinutesOfDay(currentSleepOnset);
  const deviation = circularDeviationMinutes(tonightMinutes, habitualMean);
  const baseRatio = Math.exp(-deviation / CONSISTENCY_DEVIATION_SCALE_MINUTES);
  let score = CONSISTENCY_MAX_POINTS * baseRatio;

  const variability = circularStdDevMinutes(historicalMinutes);
  const variabilityPenaltyRatio = clamp(
    variability / VARIABILITY_SCALE_MINUTES,
    0,
    MAX_VARIABILITY_PENALTY_RATIO
  );
  score *= 1 - variabilityPenaltyRatio;

  return Math.round(clamp(score, 0, CONSISTENCY_MAX_POINTS));
};

const filterScoringInterruptions = (interruptions: SleepInterruption[]): SleepInterruption[] =>
  interruptions.filter((item) => item.durationMinutes >= MIN_INTERRUPTION_MINUTES);

const calculateInterruptionScore = (interruptions: SleepInterruption[]): number => {
  const valid = filterScoringInterruptions(interruptions);
  if (valid.length === 0) return INTERRUPTION_MAX_POINTS;

  const awakeningCount = valid.length;
  const totalWasoMinutes = valid.reduce((sum, item) => sum + item.durationMinutes, 0);
  const longestAwakeningMinutes = Math.max(...valid.map((item) => item.durationMinutes));
  const weightedPenalty =
    INTERRUPTION_WASO_WEIGHT * totalWasoMinutes +
    INTERRUPTION_COUNT_WEIGHT * awakeningCount +
    INTERRUPTION_LONGEST_WEIGHT * longestAwakeningMinutes;

  const rawScore = INTERRUPTION_MAX_POINTS * Math.exp(-weightedPenalty / INTERRUPTION_PENALTY_SCALE);
  return Math.round(clamp(rawScore, 0, INTERRUPTION_MAX_POINTS));
};

// ---------------------------------------------------------------------------
// Classification & explanation
// ---------------------------------------------------------------------------

const classifySleepScore = (sleepScore: number): SleepScoreClassification => {
  if (sleepScore >= SCORE_HIGH_MIN) return 'High';
  if (sleepScore >= SCORE_MODERATE_MIN) return 'Moderate';
  return 'Low';
};

const durationPhrase = (score: number): string => {
  const ratio = score / DURATION_MAX_POINTS;
  if (ratio >= 0.9) return 'close to your sleep target';
  if (ratio >= 0.7) return 'somewhat below your sleep target';
  return 'well below your sleep target';
};

const consistencyPhrase = (score: number): string => {
  const ratio = score / CONSISTENCY_MAX_POINTS;
  if (ratio >= 0.85) return 'maintained a consistent bedtime';
  if (ratio >= 0.65) return 'had a moderately consistent bedtime';
  return 'had an inconsistent bedtime compared with recent nights';
};

const interruptionPhrase = (score: number): string => {
  const ratio = score / INTERRUPTION_MAX_POINTS;
  if (ratio >= 0.85) return 'with few nighttime awakenings';
  if (ratio >= 0.65) return 'but some nighttime awakenings reduced your score';
  return 'but frequent or long nighttime awakenings significantly reduced your score';
};

const buildSleepScoreExplanation = (
  durationScore: number,
  consistencyScore: number,
  interruptionScore: number
): string => {
  const duration = durationPhrase(durationScore);
  const consistency = consistencyPhrase(consistencyScore);
  const interruptions = interruptionPhrase(interruptionScore);

  if (interruptionScore >= INTERRUPTION_MAX_POINTS * 0.85) {
    return `You slept ${duration} and ${consistency} ${interruptions}.`;
  }
  return `You slept ${duration} and ${consistency}, ${interruptions}.`;
};

const formatDurationLabel = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

const formatClockFromMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60) % 24;
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatClockTime = (date: Date): string =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/** Human-readable breakdown of how each component score was derived. */
export const buildSleepScoreMetricDetails = (
  input: SleepScoreInput,
  scores: Pick<SleepScoreResult, 'durationScore' | 'consistencyScore' | 'interruptionScore'>
): SleepScoreMetricDetails => {
  const goalMinutes = input.sleepGoalMinutes > 0
    ? input.sleepGoalMinutes
    : DEFAULT_SLEEP_GOAL_MINUTES;
  const sleptMinutes = input.currentSession.totalSleepMinutes;

  const duration =
    `You got ${scores.durationScore}/${DURATION_MAX_POINTS} duration because your sleep goal is ${formatDurationLabel(goalMinutes)} and you slept ${formatDurationLabel(sleptMinutes)}.`;

  let consistency: string;
  if (input.previousSleepOnsets.length === 0) {
    consistency =
      `You got ${scores.consistencyScore}/${CONSISTENCY_MAX_POINTS} consistency because no prior nights were available for comparison.`;
  } else {
    const habitualMean = circularMeanMinutes(
      input.previousSleepOnsets.map(dateToMinutesOfDay)
    );
    const averageBedtime = habitualMean != null
      ? formatClockFromMinutes(habitualMean)
      : '—';
    const tonightBedtime = formatClockTime(input.currentSession.sleepOnset);
    consistency =
      `You got ${scores.consistencyScore}/${CONSISTENCY_MAX_POINTS} consistency because you fell asleep at ${tonightBedtime} and your average bedtime over the last ${input.previousSleepOnsets.length} nights was ${averageBedtime}.`;
  }

  const validInterruptions = filterScoringInterruptions(input.currentSession.interruptions);
  const awakeningCount = validInterruptions.length;
  const wasoMinutes = input.currentSession.totalWasoMinutes;
  const longestMinutes = input.currentSession.longestAwakeningMinutes;
  const awakeningLabel = awakeningCount === 1 ? 'awakening' : 'awakenings';

  const interruptions =
    `You got ${scores.interruptionScore}/${INTERRUPTION_MAX_POINTS} interruptions because you had ${awakeningCount} ${awakeningLabel}, ${wasoMinutes} min awake after sleep onset, and your longest awakening was ${longestMinutes} min.`;

  return { duration, consistency, interruptions };
};

// ---------------------------------------------------------------------------
// Session derivation from existing minute-level stage data
// ---------------------------------------------------------------------------

export const deriveSleepSessionFromPoints = (points: SleepStagePoint[]): SleepSession | null => {
  if (!Array.isArray(points) || points.length === 0) return null;

  const sorted = [...points].sort((a, b) => a.tsMs - b.tsMs);
  const sleepMinutes = sorted.filter((point) => isSleepStage(point.value));
  if (sleepMinutes.length === 0) return null;

  const sleepStart = new Date(sleepMinutes[0].tsMs);
  const sleepEnd = new Date(sleepMinutes[sleepMinutes.length - 1].tsMs);
  const sleepOnset = sleepStart;
  const totalSleepMinutes = sleepMinutes.length;

  const inBedPeriod = sorted.filter(
    (point) => point.tsMs >= sleepStart.getTime() && point.tsMs <= sleepEnd.getTime()
  );

  const interruptions: SleepInterruption[] = [];
  let index = 0;
  while (index < inBedPeriod.length) {
    if (inBedPeriod[index].value !== SLEEP_STAGE_AWAKE) {
      index += 1;
      continue;
    }

    const startMs = inBedPeriod[index].tsMs;
    let endIndex = index;
    while (endIndex < inBedPeriod.length && inBedPeriod[endIndex].value === SLEEP_STAGE_AWAKE) {
      endIndex += 1;
    }

    const durationMinutes = endIndex - index;
    if (durationMinutes >= MIN_INTERRUPTION_MINUTES) {
      interruptions.push({
        startTime: new Date(startMs),
        endTime: addMinutes(new Date(inBedPeriod[endIndex - 1].tsMs), 1),
        durationMinutes,
      });
    }
    index = endIndex;
  }

  const totalWasoMinutes = interruptions.reduce((sum, item) => sum + item.durationMinutes, 0);
  const longestAwakeningMinutes = interruptions.length > 0
    ? Math.max(...interruptions.map((item) => item.durationMinutes))
    : 0;

  return {
    sleepStart,
    sleepEnd,
    sleepOnset,
    totalSleepMinutes,
    interruptions,
    awakeningCount: interruptions.length,
    totalWasoMinutes,
    longestAwakeningMinutes,
  };
};

export const toSleepStagePoints = (
  points: Array<{ value: unknown; tsMs?: number }>
): SleepStagePoint[] =>
  points
    .filter((point) => typeof point.tsMs === 'number' && typeof point.value === 'number')
    .map((point) => ({
      tsMs: point.tsMs as number,
      value: point.value as number,
    }));

/** Gap between sleep points (minutes) above which a new night/session starts. */
export const SLEEP_SESSION_GAP_MINUTES = 60;

export type SleepNightWindow = { start: Date; end: Date };

const isValidSleepStagePoint = (point: SleepStagePoint): boolean =>
  point.value >= SLEEP_STAGE_DEEP && point.value <= SLEEP_STAGE_AWAKE;

/** Group minute-level sleep data into nights (contiguous sessions separated by long gaps). */
export const groupSleepPointsIntoSessions = (points: SleepStagePoint[]): SleepNightWindow[] => {
  const sorted = [...points]
    .filter(isValidSleepStagePoint)
    .sort((a, b) => a.tsMs - b.tsMs);
  if (sorted.length === 0) return [];

  const sessions: SleepNightWindow[] = [];
  let sessionStartMs = sorted[0].tsMs;
  let sessionEndMs = sorted[0].tsMs;

  for (let index = 1; index < sorted.length; index += 1) {
    const gapMinutes = (sorted[index].tsMs - sorted[index - 1].tsMs) / 60000;
    if (gapMinutes > SLEEP_SESSION_GAP_MINUTES) {
      sessions.push({
        start: new Date(sessionStartMs),
        end: new Date(sessionEndMs),
      });
      sessionStartMs = sorted[index].tsMs;
    }
    sessionEndMs = sorted[index].tsMs;
  }

  sessions.push({
    start: new Date(sessionStartMs),
    end: new Date(sessionEndMs),
  });
  return sessions;
};

export const findSleepSessionContaining = (
  sessions: SleepNightWindow[],
  tsMs: number
): SleepNightWindow | null =>
  sessions.find(
    (session) => tsMs >= session.start.getTime() && tsMs <= session.end.getTime()
  ) ?? null;

/** Prior completed sleep sessions before the active night, most recent first. */
export const getPreviousSleepSessionWindows = (
  sessions: SleepNightWindow[],
  activeSession: SleepNightWindow,
  count: number
): SleepNightWindow[] => {
  const activeStartMs = activeSession.start.getTime();
  return sessions
    .filter((session) => session.end.getTime() < activeStartMs)
    .sort((a, b) => b.start.getTime() - a.start.getTime())
    .slice(0, count);
};

export const getPreviousNightWindows = (
  referenceNight: { start: Date; end: Date },
  count: number
): Array<{ start: Date; end: Date }> => {
  const windows: Array<{ start: Date; end: Date }> = [];
  for (let dayOffset = 1; dayOffset <= count; dayOffset += 1) {
    const start = new Date(referenceNight.start);
    const end = new Date(referenceNight.end);
    start.setDate(start.getDate() - dayOffset);
    end.setDate(end.getDate() - dayOffset);
    windows.push({ start, end });
  }
  return windows;
};

export const extractPreviousSleepOnsets = (
  nightWindows: Array<{ start: Date; end: Date }>,
  points: SleepStagePoint[]
): Date[] => {
  const onsets: Date[] = [];
  for (const window of nightWindows) {
    const startMs = window.start.getTime();
    const endMs = window.end.getTime();
    const nightPoints = points.filter((point) => point.tsMs >= startMs && point.tsMs <= endMs);
    const session = deriveSleepSessionFromPoints(nightPoints);
    if (session) onsets.push(session.sleepOnset);
  }
  return onsets;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const validateSleepScoreInput = (input: SleepScoreInput): void => {
  if (!input?.currentSession) throw new Error('SleepScoreInput requires currentSession');

  const { currentSession, sleepGoalMinutes, previousSleepOnsets } = input;

  if (!isValidDate(currentSession.sleepOnset)) {
    throw new Error('currentSession.sleepOnset must be a valid Date');
  }
  if (!Number.isFinite(currentSession.totalSleepMinutes) || currentSession.totalSleepMinutes < 0) {
    throw new Error('currentSession.totalSleepMinutes must be a non-negative number');
  }
  if (!Number.isFinite(sleepGoalMinutes) || sleepGoalMinutes <= 0) {
    throw new Error('sleepGoalMinutes must be a positive number');
  }
  if (!Array.isArray(previousSleepOnsets)) {
    throw new Error('previousSleepOnsets must be an array');
  }
  for (const onset of previousSleepOnsets) {
    if (!isValidDate(onset)) {
      throw new Error('previousSleepOnsets must contain valid Date values');
    }
  }
};

export const computeSleepScore = (input: SleepScoreInput): SleepScoreResult => {
  validateSleepScoreInput(input);

  const goalMinutes = input.sleepGoalMinutes > 0
    ? input.sleepGoalMinutes
    : DEFAULT_SLEEP_GOAL_MINUTES;

  const durationScore = calculateDurationScore(
    input.currentSession.totalSleepMinutes,
    goalMinutes
  );
  const consistencyScore = calculateConsistencyScore(
    input.currentSession.sleepOnset,
    input.previousSleepOnsets
  );
  const interruptionScore = calculateInterruptionScore(input.currentSession.interruptions);

  const sleepScore = clamp(
    durationScore + consistencyScore + interruptionScore,
    0,
    SLEEP_SCORE_MAX
  );

  return {
    sleepScore,
    classification: classifySleepScore(sleepScore),
    durationScore,
    consistencyScore,
    interruptionScore,
    explanation: buildSleepScoreExplanation(durationScore, consistencyScore, interruptionScore),
  };
};

export const getSleepScoreColorClass = (
  sleepScore: number | null | undefined
): 'risk-green' | 'risk-yellow' | 'risk-red' | 'risk-grey' => {
  if (sleepScore == null || !Number.isFinite(sleepScore)) return 'risk-grey';
  if (sleepScore >= SCORE_HIGH_MIN) return 'risk-green';
  if (sleepScore >= SCORE_MODERATE_MIN) return 'risk-yellow';
  return 'risk-red';
};
